import { Clock, Link2 } from 'lucide-react';
import {
  getElevatedPriorityMeta,
  getStatusBadgeStyle,
  getStatusMeta,
  type TaskStatusMeta,
  type WorkspaceMember,
} from '../../lib/task-list';
import { cn } from '../../lib/utils';
import { Badge } from '../../ui/badge';
import { UserAvatar } from '../../ui/user-avatar';

/**
 * Presentational task-metadata chips shared by every task surface (list rows,
 * Kanban cards, Assignee cards, calendar panels). Rendering a task's status,
 * priority, contact, due date, or assignee anywhere MUST go through these so the
 * labels, colors, and iconography stay identical across views. See `TaskCard` for
 * the standard composition.
 */

/**
 * Priority badge — only renders for elevated priorities (Medium/High); Low/None render nothing.
 *
 * The radius comes from `--task-priority-badge-radius`, defaulting to a full pill. A host
 * theme that does not round its badges sets that variable rather than every call site
 * passing a class — the theme stays the place the decision is made. Inbox Clinic sets it
 * to 0, because there a pill means a button and nothing else is rounded.
 */
export function TaskPriorityBadge({
  priority,
  className,
}: {
  priority: number | null;
  className?: string;
}) {
  const meta = getElevatedPriorityMeta(priority);
  if (!meta) return null;
  return (
    <Badge
      variant="outline"
      className={cn(
        'rounded-[var(--task-priority-badge-radius,calc(infinity*1px))] border px-2 py-0.5 text-[10px] font-bold',
        meta.className,
        className
      )}
    >
      {meta.label}
    </Badge>
  );
}

/** Workspace-status pill, colored from the status's configured color. */
export function TaskStatusBadge({
  statuses,
  statusKey,
  className,
}: {
  statuses: TaskStatusMeta[];
  statusKey: string | null;
  className?: string;
}) {
  const meta = getStatusMeta(statuses, statusKey);
  if (!meta) return null;
  return (
    <Badge
      variant="outline"
      className={cn(
        'rounded-md border px-1.5 py-0.5 text-[10px] font-normal capitalize',
        className
      )}
      style={getStatusBadgeStyle(statuses, statusKey)}
    >
      {meta.label}
    </Badge>
  );
}

/** Linked-contact chip (chain icon + name). Renders nothing when the task is standalone. */
export function TaskContactChip({
  contact,
  className,
}: {
  contact?: { label: string; displayName: string } | null;
  className?: string;
}) {
  if (!contact) return null;
  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-1 text-[10px] text-muted-foreground/80',
        className
      )}
    >
      <Link2 className="h-2.5 w-2.5 shrink-0 text-primary/70" />
      <span className="truncate">{contact.displayName || contact.label}</span>
    </span>
  );
}

/** Due-date chip. Turns red when overdue and the task isn't done. Renders nothing with no date. */
export function TaskDueDate({
  dueAt,
  done = false,
  showEmpty = false,
  className,
}: {
  dueAt: number | null;
  done?: boolean;
  /** Render "No date" instead of nothing when there's no due date (for column layouts). */
  showEmpty?: boolean;
  className?: string;
}) {
  if (!dueAt) {
    if (!showEmpty) return null;
    return (
      <span className={cn('flex items-center gap-1 text-[11px] text-muted-foreground', className)}>
        <Clock className="h-3 w-3 opacity-60" />
        No date
      </span>
    );
  }
  const overdue = !done && dueAt * 1000 < Date.now();
  return (
    <span
      className={cn(
        'flex items-center gap-1 text-[11px]',
        overdue ? 'font-medium text-red-600 dark:text-red-400' : 'text-muted-foreground',
        className
      )}
    >
      <Clock className="h-3 w-3 opacity-60" />
      {new Date(dueAt * 1000).toLocaleDateString()}
    </span>
  );
}

/**
 * The completion affordance. One implementation so "complete" looks the same on a card as it
 * does in a list row — these had drifted into a filled square and a 45°-rotated `Plus` icon.
 */
export function TaskCompletionToggle({
  done,
  onToggle,
  disabled = false,
  className,
}: {
  done: boolean;
  /** Omit to render the mark as a non-interactive indicator (list rows that toggle elsewhere). */
  onToggle?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const mark = done ? <div className="h-2 w-2 rounded-[1px] bg-primary-foreground" /> : null;
  const shape = cn(
    'h-4 w-4 shrink-0 rounded border flex items-center justify-center transition-colors',
    done ? 'bg-primary border-primary' : 'border-border bg-background',
    className
  );

  if (!onToggle) {
    return <div className={shape}>{mark}</div>;
  }

  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={done}
      aria-label={done ? 'Mark task incomplete' : 'Mark task complete'}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(shape, 'disabled:cursor-default')}
    >
      {mark}
    </button>
  );
}

/** Assignee avatar (initial). Renders nothing when unassigned. */
export function TaskAssigneeAvatar({
  member,
  className,
}: {
  member: WorkspaceMember | null;
  className?: string;
}) {
  if (!member) return null;
  return <UserAvatar user={member} size="xs" className={className} />;
}
